/**
 * DeletePortfolioModal — delete an aggregation / portfolio (SHA-43 portfolio CRUD).
 *
 * Calls DELETE /api/aggregations/:id. Warns the user that all linked backtest
 * runs, grid searches, walk-forward runs, and paper sessions will be removed
 * along with the portfolio — this matches the cascade behaviour on the server.
 *
 * Mirrors DeleteStrategyModal but operates on aggregations, not user strategies.
 */

import { useState } from 'react';
import { Modal } from '../Modal/Modal.js';
import { useDeleteAggregation } from '../../hooks/useBacktest.js';

interface DeletePortfolioModalProps {
  isOpen: boolean;
  portfolioId: string;
  portfolioName: string;
  onClose: () => void;
  /** Called only when the delete succeeds — e.g. to clear the selection. */
  onDeleted?: () => void;
}

export function DeletePortfolioModal({
  isOpen,
  portfolioId,
  portfolioName,
  onClose,
  onDeleted,
}: DeletePortfolioModalProps) {
  const [error, setError] = useState<string | null>(null);
  const { mutateAsync, isPending } = useDeleteAggregation();

  const handleDelete = async () => {
    setError(null);
    try {
      await mutateAsync(portfolioId);
      onDeleted?.();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete portfolio');
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Delete Portfolio" size="md">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <p style={{ margin: 0, fontSize: 13.5, color: '#ddd' }}>
          Delete <strong>{portfolioName}</strong>? This cannot be undone from the UI.
        </p>

        <div
          style={{
            padding: '10px 12px',
            background: 'rgba(250,204,21,0.10)',
            border: '1px solid rgba(250,204,21,0.30)',
            borderRadius: 6,
            fontSize: 12.5,
            color: '#facc15',
          }}
        >
          Deleting this portfolio will also remove all linked backtest runs, grid search
          results, walk-forward runs, and paper sessions that belong to it.
        </div>

        {error && (
          <div style={{ fontSize: 12.5, color: '#f87171' }}>{error}</div>
        )}

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 4 }}>
          <button
            onClick={onClose}
            style={{
              padding: '7px 14px',
              background: 'none',
              border: '1px solid #444',
              borderRadius: 6,
              color: '#aaa',
              fontSize: 13,
              cursor: 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            onClick={() => void handleDelete()}
            disabled={isPending}
            style={{
              padding: '7px 16px',
              background: '#c0392b',
              border: 'none',
              borderRadius: 6,
              color: '#fff',
              fontSize: 13,
              fontWeight: 600,
              cursor: isPending ? 'not-allowed' : 'pointer',
              opacity: isPending ? 0.6 : 1,
            }}
          >
            {isPending ? 'Deleting...' : 'Delete'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
